/**
 * TaskFilters - Filter controls and view switching for the Tasks module.
 *
 * Provides:
 * - View switcher (All, Today, Upcoming, List, Matrix) via Tabs component
 * - Priority dropdown filter
 * - List selector dropdown
 * - Tag filter with multi-select chips
 * - Status toggle (show/hide completed)
 * - Debounced text search (300ms)
 * - Date range inputs (start/end)
 *
 * @see Requirements 3.1, 3.2, 3.4, 3.5, 3.6, 3.7
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTaskStore, type TaskView } from '@/stores/taskStore';
import { Tabs, type Tab } from '@/components/ui/Tabs';
import { Badge } from '@/components/ui/Badge';
import type { Priority } from '@/types';

export interface TaskFiltersProps {
  className?: string;
}

/** Debounce delay for text search in ms. */
const SEARCH_DEBOUNCE_MS = 300;

/** Views available in the Tasks module. */
const VIEW_TABS: Tab[] = [
  { id: 'inbox', label: 'All' },
  { id: 'today', label: 'Today' },
  { id: 'upcoming', label: 'Next 7 Days' },
  { id: 'list', label: 'List' },
  { id: 'eisenhower', label: 'Matrix' },
];

/** Priority options for the filter dropdown. */
const PRIORITY_OPTIONS: { value: Priority | 'all'; label: string }[] = [
  { value: 'all', label: 'All Priorities' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: 'none', label: 'None' },
];

export function TaskFilters({ className = '' }: TaskFiltersProps) {
  const {
    filters,
    activeView,
    taskLists,
    tags,
    setFilters,
    setActiveView,
    loadTaskLists,
    loadTags,
  } = useTaskStore();

  // Local state for debounced search
  const [searchText, setSearchText] = useState(filters.searchText ?? '');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load task lists and tags on mount
  useEffect(() => {
    loadTaskLists();
    loadTags();
  }, [loadTaskLists, loadTags]);

  // Sync local search text when external filters change
  useEffect(() => {
    setSearchText(filters.searchText ?? '');
  }, [filters.searchText]);

  // ─── Debounced Search ──────────────────────────────────────────────────────

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value);

      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }

      searchTimerRef.current = setTimeout(() => {
        setFilters({ ...filters, searchText: value || undefined });
      }, SEARCH_DEBOUNCE_MS);
    },
    [filters, setFilters]
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  // ─── View Change ──────────────────────────────────────────────────────────

  const handleViewChange = useCallback(
    (viewId: string) => {
      setActiveView(viewId as TaskView);
    },
    [setActiveView]
  );

  // ─── Priority Filter ──────────────────────────────────────────────────────

  const handlePriorityChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      const priority = value === 'all' ? undefined : (value as Priority);
      setFilters({ ...filters, priority });
    },
    [filters, setFilters]
  );

  // ─── List Filter ──────────────────────────────────────────────────────────

  const handleListChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      const listId = value === 'all' ? undefined : value;
      setFilters({ ...filters, listId });
    },
    [filters, setFilters]
  );

  // ─── Tag Filter ───────────────────────────────────────────────────────────

  const selectedTagIds = useMemo(() => filters.tagIds ?? [], [filters.tagIds]);

  const handleTagToggle = useCallback(
    (tagId: string) => {
      const current = filters.tagIds ?? [];
      const updated = current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId];
      setFilters({ ...filters, tagIds: updated.length > 0 ? updated : undefined });
    },
    [filters, setFilters]
  );

  // ─── Status Filter ────────────────────────────────────────────────────────

  const handleStatusToggle = useCallback(() => {
    const current = filters.isCompleted;
    // Cycle: undefined (show all) → false (hide completed) → undefined
    const next = current === undefined ? false : undefined;
    setFilters({ ...filters, isCompleted: next });
  }, [filters, setFilters]);

  // ─── Date Range Filter ────────────────────────────────────────────────────

  const handleDateStartChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFilters({ ...filters, dueDateStart: value || undefined });
    },
    [filters, setFilters]
  );

  const handleDateEndChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFilters({ ...filters, dueDateEnd: value || undefined });
    },
    [filters, setFilters]
  );

  return (
    <div className={['space-y-4', className].join(' ')}>
      {/* View Switcher */}
      <Tabs
        tabs={VIEW_TABS}
        activeTab={activeView}
        onChange={handleViewChange}
        className="w-full"
      />

      {/* Filter Controls */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Text Search */}
        <div className="relative flex-1 min-w-[200px]">
          <label htmlFor="task-search" className="sr-only">
            Search tasks
          </label>
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            id="task-search"
            type="search"
            placeholder="Search tasks..."
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
            className={[
              'w-full pl-9 pr-3 py-2 text-sm rounded-lg',
              'bg-bg-secondary border border-border',
              'text-text-primary placeholder:text-text-tertiary',
              'transition-colors duration-default ease-default',
              'focus:outline-none focus:ring-2 focus:ring-focus-ring focus:border-transparent',
            ].join(' ')}
          />
        </div>

        {/* Priority Filter */}
        <div>
          <label htmlFor="filter-priority" className="sr-only">
            Filter by priority
          </label>
          <select
            id="filter-priority"
            value={filters.priority ?? 'all'}
            onChange={handlePriorityChange}
            className={[
              'px-3 py-2 text-sm rounded-lg',
              'bg-bg-secondary border border-border',
              'text-text-primary',
              'transition-colors duration-default ease-default',
              'focus:outline-none focus:ring-2 focus:ring-focus-ring focus:border-transparent',
              'cursor-pointer',
            ].join(' ')}
          >
            {PRIORITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* List Filter */}
        <div>
          <label htmlFor="filter-list" className="sr-only">
            Filter by list
          </label>
          <select
            id="filter-list"
            value={filters.listId ?? 'all'}
            onChange={handleListChange}
            className={[
              'px-3 py-2 text-sm rounded-lg',
              'bg-bg-secondary border border-border',
              'text-text-primary',
              'transition-colors duration-default ease-default',
              'focus:outline-none focus:ring-2 focus:ring-focus-ring focus:border-transparent',
              'cursor-pointer',
            ].join(' ')}
          >
            <option value="all">All Lists</option>
            {taskLists.map((list) => (
              <option key={list.listId} value={list.listId}>
                {list.name}
              </option>
            ))}
          </select>
        </div>

        {/* Status Toggle */}
        <button
          type="button"
          onClick={handleStatusToggle}
          aria-pressed={filters.isCompleted === false}
          className={[
            'px-3 py-2 text-sm rounded-lg',
            'border transition-colors duration-default ease-default',
            'focus:outline-none focus:ring-2 focus:ring-focus-ring focus:ring-offset-1',
            filters.isCompleted === false
              ? 'bg-primary-light border-primary text-text-primary'
              : 'bg-bg-secondary border-border text-text-secondary hover:text-text-primary hover:bg-hover-bg',
          ].join(' ')}
        >
          {filters.isCompleted === false ? 'Completed hidden' : 'Hide completed'}
        </button>

        {/* Date Range */}
        <div className="flex items-center gap-2">
          <label htmlFor="filter-date-start" className="sr-only">
            Due date from
          </label>
          <input
            id="filter-date-start"
            type="date"
            value={filters.dueDateStart ?? ''}
            onChange={handleDateStartChange}
            placeholder="From"
            className={[
              'px-3 py-2 text-sm rounded-lg',
              'bg-bg-secondary border border-border',
              'text-text-primary',
              'transition-colors duration-default ease-default',
              'focus:outline-none focus:ring-2 focus:ring-focus-ring focus:border-transparent',
            ].join(' ')}
          />
          <span className="text-text-tertiary text-sm" aria-hidden="true">–</span>
          <label htmlFor="filter-date-end" className="sr-only">
            Due date to
          </label>
          <input
            id="filter-date-end"
            type="date"
            value={filters.dueDateEnd ?? ''}
            onChange={handleDateEndChange}
            placeholder="To"
            className={[
              'px-3 py-2 text-sm rounded-lg',
              'bg-bg-secondary border border-border',
              'text-text-primary',
              'transition-colors duration-default ease-default',
              'focus:outline-none focus:ring-2 focus:ring-focus-ring focus:border-transparent',
            ].join(' ')}
          />
        </div>
      </div>

      {/* Tag Filter Chips */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by tags">
          {tags.map((tag) => {
            const isSelected = selectedTagIds.includes(tag.tagId);
            return (
              <button
                key={tag.tagId}
                type="button"
                onClick={() => handleTagToggle(tag.tagId)}
                aria-pressed={isSelected}
                className={[
                  'transition-all duration-default ease-default',
                  'focus:outline-none focus:ring-2 focus:ring-focus-ring focus:ring-offset-1',
                  'rounded-full',
                  isSelected ? 'ring-2 ring-primary ring-offset-1' : '',
                ].join(' ')}
              >
                <Badge
                  text={tag.name}
                  color={tag.color}
                  variant={isSelected ? 'filled' : 'outlined'}
                  size="sm"
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
